import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { upscaleApi } from '@/features/design/album/api/upscale';
import { toast } from '@/shared/lib/toast';
import { useJobQueueStore } from '@/features/design/job-queue';
import { isUpscaleJob } from '@/features/design/job-queue/model/job-queue.types';
import { RenderStatus } from '@/shared/constants/status';
import { calculateTimeBasedProgress } from '@/shared/lib/render-status';
import { parseApiError } from '../lib/error-utils';

/**
 * Modern upscale hook that reads from Job Queue Store (single source of truth)
 * Replaces the old use-upscale.ts which used React Query for tracking
 */
export function useUpscaleProgress() {
  const jobQueue = useJobQueueStore((state) => state.jobQueue);
  const [tick, setTick] = useState(0);

  // Check if there are any active upscale jobs that need progress tracking
  const hasActiveUpscaleJobs = jobQueue.some(
    (job) =>
      isUpscaleJob(job) &&
      (job.status === RenderStatus.PENDING ||
        job.status === RenderStatus.PROCESSING)
  );

  // Only run the interval when there are active upscale jobs
  useEffect(() => {
    if (!hasActiveUpscaleJobs) return;

    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [hasActiveUpscaleJobs]);

  // Mutation for starting upscale. Locks on the source render id so a WS
  // event for that render can't race the server-side upscale start.
  const upscaleMutation = useMutation({
    mutationKey: ['renders', 'upscale'],
    meta: { lockId: (vars: unknown) => vars as string },
    mutationFn: async (renderId: string) => {
      // Check if already upscaling via Job Queue Store
      const existingJob = jobQueue.find(
        (job) =>
          isUpscaleJob(job) &&
          job.originalRenderId === renderId &&
          (job.status === RenderStatus.PENDING ||
            job.status === RenderStatus.PROCESSING)
      );

      if (existingJob) {
        throw new Error('This render is already being upscaled');
      }

      const response = await upscaleApi.upscaleRender(renderId);
      return { renderId, data: response.data };
    },
    onError: (error) => {
      // Handle error with unified error parser
      const errorDetails = parseApiError(
        error,
        'Failed to start upscale process'
      );

      // Special handling for already upscaling error
      if (errorDetails.message === 'This render is already being upscaled') {
        toast.warning(errorDetails.message);
      } else if (errorDetails.isTokenError) {
        // Show detailed token error
        toast.error(errorDetails.message);
      } else {
        toast.error(errorDetails.message);
      }
    },
  });

  /**
   * Check if a render is currently being upscaled
   * Note: tick forces component re-renders for progress updates, callbacks stay stable
   */
  const isUpscaling = useCallback(
    (renderId: string): boolean => {
      const job = jobQueue.find(
        (j) =>
          isUpscaleJob(j) &&
          j.originalRenderId === renderId &&
          (j.status === RenderStatus.PENDING ||
            j.status === RenderStatus.PROCESSING)
      );
      return !!job;
    },
    [jobQueue]
  );

  /**
   * Get progress percentage for a render being upscaled (0-95)
   * Called during render, so tick changes trigger fresh calculations
   */
  const getUpscaleProgress = useCallback(
    (renderId: string): number => {
      const job = jobQueue.find(
        (j) =>
          isUpscaleJob(j) &&
          j.originalRenderId === renderId &&
          j.status === RenderStatus.PROCESSING
      );

      if (!job || !job.startedAt) return 0;

      return calculateTimeBasedProgress(
        job.startedAt,
        job.durationPredicted,
        job.id
      );
    },
    [jobQueue]
  );

  // tick forces re-render every second when active
  void tick;

  /**
   * Get upscale job status for a render
   */
  const getUpscaleStatus = useCallback(
    (renderId: string) => {
      const job = jobQueue.find(
        (job) => isUpscaleJob(job) && job.originalRenderId === renderId
      );

      if (!job) return null;

      return {
        status: job.status,
        progress: getUpscaleProgress(renderId),
        startedAt: job.startedAt,
      };
    },
    [jobQueue, getUpscaleProgress]
  );

  /**
   * Get all upscale jobs
   */
  const upscaleJobs = jobQueue.filter(isUpscaleJob);

  /**
   * Check if any upscale is in progress
   */
  const isUpscalingAny =
    upscaleMutation.isPending ||
    upscaleJobs.some(
      (job) =>
        job.status === RenderStatus.PENDING ||
        job.status === RenderStatus.PROCESSING
    );

  return {
    upscaleRender: upscaleMutation.mutate,
    isUpscaling,
    getUpscaleProgress,
    getUpscaleStatus,
    upscaleJobs,
    isUpscalingAny,
  };
}
